import axiosForBackend from './axios';
import './axios';
import type {
  Order, Player, Level, Product, Recharge, Salary,
  Deduction, Bonus, DepositLog, OtherExpense, ProductPurchase, ProductStockIn,
  DashboardData, PaginatedResponse, WithdrawalRequest,
  TierLevel,
  CreateOrderRequest, CreatePlayerRequest, UpdatePlayerRequest,
  CreateRechargeRequest, CreateSalaryRequest,
  CreateDeductionRequest, CreateBonusRequest, CreateDepositLogRequest,
  CreateOtherExpenseRequest,
  CreateProductRequest, UpdateProductRequest, CreateStockInRequest,
  CreatePurchaseRequest, CreateLevelRequest, UpdateLevelRequest,
  User, CreateUserRequest, UpdateUserRequest,
  QuickSaleRequest, QuickSaleResult,
  CreateWithdrawalRequest, ProcessWithdrawalRequest,
  UpdateQrCodeRequest,
  PlayerTransactionListResponse,
  BossSummary,
  BossDetail,
  UpdateBossRequest,
  Coupon,
  CreateCouponRequest,
  UpdateCouponRequest,
  GrantCouponRequest,
  BossCouponItem,
  BossBenefits,
  LevelType,
  GameCount,
  PlayerRevenueRanking,
} from '@shared/api.interface';
import { login, getCurrentUser, changePassword, getProfile } from './auth';
import {
  getUsers, createUser, updateUser, deleteUser,
  resetUserPassword, toggleUserStatus,
} from './users';

function getQueryString(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      qs.append(k, String(v));
    }
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// Dashboard
export async function getDashboard(): Promise<DashboardData> {
  const res = await axiosForBackend.get('/api/dashboard/stats');
  return res.data;
}

// Levels
export async function getLevels(type?: LevelType): Promise<Level[]> {
  const res = await axiosForBackend.get(`/api/levels${getQueryString({ type })}`);
  return res.data;
}

export async function createLevel(data: CreateLevelRequest): Promise<Level> {
  const res = await axiosForBackend.post('/api/levels', data);
  return res.data;
}

export async function updateLevel(id: string, data: UpdateLevelRequest): Promise<Level> {
  const res = await axiosForBackend.patch(`/api/levels/${id}`, data);
  return res.data;
}

export async function deleteLevel(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/levels/${id}`);
}

// Players
export async function getPlayers(params: {
  page?: number; pageSize?: number; search?: string;
} = {}): Promise<PaginatedResponse<Player>> {
  const res = await axiosForBackend.get(`/api/players${getQueryString(params)}`);
  return res.data;
}

export async function getAllPlayers(): Promise<Player[]> {
  const res = await axiosForBackend.get('/api/players/all');
  return res.data;
}

export async function getSimplePlayers(): Promise<Array<{ nickname: string; level: string; game: string }>> {
  const res = await axiosForBackend.get('/api/players/simple');
  return res.data;
}

export async function createPlayer(data: CreatePlayerRequest): Promise<Player> {
  const res = await axiosForBackend.post('/api/players', data);
  return res.data;
}

export async function updatePlayer(id: string, data: UpdatePlayerRequest): Promise<Player> {
  const res = await axiosForBackend.patch(`/api/players/${id}`, data);
  return res.data;
}

export async function deletePlayer(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/players/${id}`);
}

export async function getPlayerTransactions(params: {
  playerId?: string;
  type?: string;
} = {}): Promise<PlayerTransactionListResponse> {
  const path = params.playerId
    ? `/api/players/${params.playerId}/transactions`
    : '/api/players/me/transactions';
  const res = await axiosForBackend.get(`${path}${getQueryString({ type: params.type })}`);
  return res.data;
}

// Orders
export async function getPlayerRevenueRanking(): Promise<PlayerRevenueRanking> {
  const res = await axiosForBackend.get('/api/players/rankings');
  return res.data;
}

export async function getOrders(params: {
  page?: number; pageSize?: number; bossName?: string;
  playerName?: string; startDate?: string; endDate?: string;
  status?: string; note?: string;
} = {}): Promise<PaginatedResponse<Order>> {
  const res = await axiosForBackend.get(`/api/orders${getQueryString(params)}`);
  return res.data;
}

// 订单数据版本（变化检测用，轻量接口）
export async function getOrdersVersion(): Promise<{ maxUpdatedAt: string; count: number }> {
  const res = await axiosForBackend.get('/api/orders/version');
  return res.data;
}

export async function createOrder(data: CreateOrderRequest): Promise<Order> {
  const res = await axiosForBackend.post('/api/orders', data);
  return res.data;
}

export async function updateOrder(id: string, data: Partial<CreateOrderRequest>): Promise<Order> {
  const res = await axiosForBackend.put(`/api/orders/${id}`, data);
  return res.data;
}

export async function settleOrder(id: string, settleNote: string, settleImage: string): Promise<Order> {
  const res = await axiosForBackend.post(`/api/orders/${id}/settle`, { settleNote, settleImage });
  return res.data;
}

export async function cancelRequestOrder(id: string, reason: string): Promise<Order> {
  const res = await axiosForBackend.post(`/api/orders/${id}/cancel-request`, { reason });
  return res.data;
}

export async function reviewOrder(id: string, approve: boolean): Promise<Order> {
  const res = await axiosForBackend.post(`/api/orders/${id}/review`, { approve });
  return res.data;
}

export async function getGameCounts(): Promise<GameCount[]> {
  const res = await axiosForBackend.get('/api/orders/game-counts');
  return res.data;
}

export async function deleteOrder(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/orders/${id}`);
}

// Finance - Recharge
export async function getRecharges(params: {
  page?: number; pageSize?: number; bossName?: string;
} = {}): Promise<PaginatedResponse<Recharge>> {
  const res = await axiosForBackend.get(`/api/finance/recharges${getQueryString(params)}`);
  return res.data;
}

export async function createRecharge(data: CreateRechargeRequest): Promise<Recharge> {
  const res = await axiosForBackend.post('/api/finance/recharge', data);
  return res.data;
}

export async function deleteRecharge(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/finance/recharge/${id}`);
}

// Finance - Salary
export async function getSalaries(params: {
  page?: number; pageSize?: number; playerName?: string;
} = {}): Promise<PaginatedResponse<Salary>> {
  const res = await axiosForBackend.get(`/api/finance/salaries${getQueryString(params)}`);
  return res.data;
}

export async function createSalary(data: CreateSalaryRequest): Promise<Salary> {
  const res = await axiosForBackend.post('/api/finance/salary', data);
  return res.data;
}

export async function deleteSalary(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/finance/salary/${id}`);
}

// Finance - Deduction
export async function getDeductions(params: {
  page?: number; pageSize?: number; playerName?: string;
} = {}): Promise<PaginatedResponse<Deduction>> {
  const res = await axiosForBackend.get(`/api/finance/deductions${getQueryString(params)}`);
  return res.data;
}

export async function createDeduction(data: CreateDeductionRequest): Promise<Deduction> {
  const res = await axiosForBackend.post('/api/finance/deduction', data);
  return res.data;
}

export async function deleteDeduction(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/finance/deduction/${id}`);
}

// Finance - Other Expense
export async function getOtherExpenses(params: {
  page?: number; pageSize?: number;
} = {}): Promise<PaginatedResponse<OtherExpense>> {
  const res = await axiosForBackend.get(`/api/finance/other-expenses${getQueryString(params)}`);
  return res.data;
}

export async function createOtherExpense(data: CreateOtherExpenseRequest): Promise<OtherExpense> {
  const res = await axiosForBackend.post('/api/finance/other-expense', data);
  return res.data;
}

export async function deleteOtherExpense(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/finance/other-expense/${id}`);
}

// Finance - Bonus
export async function getBonuses(params: {
  page?: number; pageSize?: number; playerName?: string;
} = {}): Promise<PaginatedResponse<Bonus>> {
  const res = await axiosForBackend.get(`/api/finance/bonuses${getQueryString(params)}`);
  return res.data;
}

export async function createBonus(data: CreateBonusRequest): Promise<Bonus> {
  const res = await axiosForBackend.post('/api/finance/bonus', data);
  return res.data;
}

export async function deleteBonus(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/finance/bonus/${id}`);
}

// Finance - 业绩激励
export async function getPerfConfig(): Promise<{ threshold: number; percent: number }[]> {
  const res = await axiosForBackend.get('/api/finance/performance-rewards/config');
  return res.data;
}

export async function updatePerfConfig(configs: { threshold: number; percent: number }[]): Promise<{ success: boolean }> {
  const res = await axiosForBackend.put('/api/finance/performance-rewards/config', { configs });
  return res.data;
}

export async function getPerfRewards(week: string): Promise<any> {
  const res = await axiosForBackend.get(`/api/finance/performance-rewards?week=${week}`);
  return res.data;
}

export async function grantPerfRewards(week: string, playerNames: string[]): Promise<{ success: boolean; granted: { nickname: string; amount: number }[]; skipped: string[] }> {
  const res = await axiosForBackend.post('/api/finance/performance-rewards/grant', { week, playerNames });
  return res.data;
}

// Finance - Deposit
export async function getDepositLogs(params: {
  page?: number; pageSize?: number; playerName?: string;
} = {}): Promise<PaginatedResponse<DepositLog>> {
  const res = await axiosForBackend.get(`/api/finance/deposit-logs${getQueryString(params)}`);
  return res.data;
}

export async function createDepositLog(data: CreateDepositLogRequest): Promise<DepositLog> {
  const res = await axiosForBackend.post('/api/finance/deposit', data);
  return res.data;
}

export async function deleteDepositLog(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/finance/deposit-log/${id}`);
}

// Products
export async function getProducts(params: {
  page?: number; pageSize?: number; search?: string;
} = {}): Promise<PaginatedResponse<Product>> {
  const res = await axiosForBackend.get(`/api/products${getQueryString(params)}`);
  return res.data;
}

export async function getAllProducts(): Promise<Product[]> {
  const res = await axiosForBackend.get('/api/products/all');
  return res.data;
}

export async function getLowStockProducts(): Promise<Product[]> {
  const res = await axiosForBackend.get('/api/products/low-stock');
  return res.data;
}

export async function createProduct(data: CreateProductRequest): Promise<Product> {
  const res = await axiosForBackend.post('/api/products', data);
  return res.data;
}

export async function updateProduct(id: string, data: UpdateProductRequest): Promise<Product> {
  const res = await axiosForBackend.patch(`/api/products/${id}`, data);
  return res.data;
}

export async function deleteProduct(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/products/${id}`);
}

export async function getStockInList(params: {
  page?: number; pageSize?: number; productName?: string;
} = {}): Promise<PaginatedResponse<ProductStockIn>> {
  const res = await axiosForBackend.get(`/api/products/stock-in/list${getQueryString(params)}`);
  return res.data;
}

export async function createStockIn(data: CreateStockInRequest): Promise<ProductStockIn> {
  const res = await axiosForBackend.post('/api/products/stock-in', data);
  return res.data;
}

export async function deleteStockIn(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/products/stock-in/${id}`);
}

export async function getPurchaseList(params: {
  page?: number; pageSize?: number; productName?: string; playerName?: string;
} = {}): Promise<PaginatedResponse<ProductPurchase>> {
  const res = await axiosForBackend.get(`/api/products/purchase/list${getQueryString(params)}`);
  return res.data;
}

export async function createPurchase(data: CreatePurchaseRequest): Promise<ProductPurchase> {
  const res = await axiosForBackend.post('/api/products/purchase', data);
  return res.data;
}

export async function deletePurchase(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/products/purchase/${id}`);
}

export async function quickSale(data: QuickSaleRequest): Promise<QuickSaleResult> {
  const res = await axiosForBackend.post('/api/products/quick-sale', data);
  return res.data;
}

export async function createLoss(data: CreateLossRequest): Promise<ProductLoss> {
  const res = await axiosForBackend.post('/api/products/loss', data);
  return res.data;
}

export async function getLossList(params: {
  page?: number; pageSize?: number; productName?: string;
} = {}): Promise<PaginatedResponse<ProductLoss>> {
  const res = await axiosForBackend.get(`/api/products/loss/list${getQueryString(params)}`);
  return res.data;
}

// Auth
export { login, getCurrentUser, changePassword, getProfile };

// Users
export {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  toggleUserStatus,
};

export type { User, CreateUserRequest, UpdateUserRequest };

// Bosses
export async function getBosses(params: {
  search?: string; page?: number; pageSize?: number;
} = {}): Promise<PaginatedResponse<BossSummary>> {
  const res = await axiosForBackend.get(`/api/bosses${getQueryString(params)}`);
  return res.data;
}

export async function getBossDetail(name: string, code?: number): Promise<BossDetail> {
  const query = code ? getQueryString({ code }) : getQueryString({ name });
  const res = await axiosForBackend.get(`/api/bosses/detail${query}`);
  return res.data;
}

export async function getBossOptions(): Promise<{ code: number | null; name: string }[]> {
  const res = await axiosForBackend.get('/api/bosses/options');
  return res.data;
}

// 读取客户分层配置（type=boss 老板 / player 陪玩）
export async function getTierConfig(type: 'boss' | 'player'): Promise<TierLevel[]> {
  const res = await axiosForBackend.get('/api/settings/tier-config', { params: { type } });
  return res.data?.tiers || [];
}

export async function getPlayersAll(): Promise<any[]> {
  const res = await axiosForBackend.get('/api/players/all');
  return res.data;
}

export async function createBoss(data: {
  name: string;
  phone?: string;
  wechat?: string;
  note?: string;
  bossLevel?: string;
}): Promise<BossSummary> {
  const res = await axiosForBackend.post('/api/bosses', data);
  return res.data;
}

export async function updateBoss(id: string, data: UpdateBossRequest): Promise<BossSummary> {
  const res = await axiosForBackend.patch(`/api/bosses/${id}`, data);
  return res.data;
}

export async function deleteBoss(id: string): Promise<{ success: true }> {
  const res = await axiosForBackend.delete(`/api/bosses/${id}`);
  return res.data;
}

// 合并老板账号：source 数据并入 target 后软删，finalName 为最终名称（默认保留 target 原名）
export async function mergeBosses(targetId: string, sourceId: string, finalName?: string) {
  const res = await axiosForBackend.post('/api/bosses/merge', { targetId, sourceId, finalName });
  return res.data;
}

// Coupons
export async function getCoupons(): Promise<Coupon[]> {
  const res = await axiosForBackend.get('/api/coupons');
  return res.data;
}

export async function createCoupon(data: CreateCouponRequest): Promise<Coupon> {
  const res = await axiosForBackend.post('/api/coupons', data);
  return res.data;
}

export async function updateCoupon(id: string, data: UpdateCouponRequest): Promise<Coupon> {
  const res = await axiosForBackend.patch(`/api/coupons/${id}`, data);
  return res.data;
}

export async function deleteCoupon(id: string): Promise<void> {
  await axiosForBackend.delete(`/api/coupons/${id}`);
}

export async function grantCoupon(data: GrantCouponRequest): Promise<{ granted: number }> {
  const res = await axiosForBackend.post('/api/coupons/grant', data);
  return res.data;
}

export async function getBossCoupons(bossId: string): Promise<BossCouponItem[]> {
  const res = await axiosForBackend.get(`/api/coupons/boss/${bossId}`);
  return res.data;
}

export async function getBossBenefits(bossName: string): Promise<BossBenefits> {
  const res = await axiosForBackend.get(`/api/coupons/benefits${getQueryString({ bossName })}`);
  return res.data;
}

// Withdrawals
export async function getWithdrawals(params: {
  page?: number; pageSize?: number; status?: string; playerName?: string;
} = {}): Promise<PaginatedResponse<WithdrawalRequest>> {
  const res = await axiosForBackend.get(`/api/withdrawals${getQueryString(params)}`);
  return res.data;
}

export async function getMyWithdrawals(params: {
  page?: number; pageSize?: number;
} = {}): Promise<PaginatedResponse<WithdrawalRequest>> {
  const res = await axiosForBackend.get(`/api/withdrawals/mine${getQueryString(params)}`);
  return res.data;
}

export async function createWithdrawal(data: CreateWithdrawalRequest): Promise<WithdrawalRequest> {
  const res = await axiosForBackend.post('/api/withdrawals', data);
  return res.data;
}

export async function approveWithdrawal(id: string, data: ProcessWithdrawalRequest = {}): Promise<WithdrawalRequest> {
  const res = await axiosForBackend.post(`/api/withdrawals/${id}/approve`, data);
  return res.data;
}

export async function rejectWithdrawal(id: string, data: ProcessWithdrawalRequest): Promise<WithdrawalRequest> {
  const res = await axiosForBackend.post(`/api/withdrawals/${id}/reject`, data);
  return res.data;
}

export async function updateUserQrCode(data: UpdateQrCodeRequest): Promise<{ success: true }> {
  const res = await axiosForBackend.patch('/api/auth/qr-code', data);
  return res.data;
}

export async function uploadWechatQr(file: File): Promise<{ success: true }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axiosForBackend.post('/api/auth/upload-wechat-qr', fd);
  return res.data;
}

export async function uploadAlipayQr(file: File): Promise<{ success: true }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axiosForBackend.post('/api/auth/upload-alipay-qr', fd);
  return res.data;
}


// 补全：通知接口
export async function getNotifications(params: { unreadOnly?: boolean } = {}) {
  const res = await axiosForBackend.get(`/api/notifications${getQueryString(params)}`);
  return res.data;
}
export async function getUnreadCount() {
  const res = await axiosForBackend.get('/api/notifications/unread-count');
  return res.data;
}
export async function markNotificationRead(id: string) {
  const res = await axiosForBackend.post(`/api/notifications/${id}/read`);
  return res.data;
}
export async function markAllNotificationsRead() {
  const res = await axiosForBackend.post('/api/notifications/read-all');
  return res.data;
}

// 补全：备份接口
export async function getBackupList() {
  const res = await axiosForBackend.get('/api/backup/list');
  return res.data;
}
export async function createBackup() {
  const res = await axiosForBackend.get('/api/backup/create');
  return res.data;
}


// 补全：上传头像（陪玩/管理员自己）
export async function uploadAvatar(file: File) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axiosForBackend.post('/api/auth/upload-avatar', fd);
  return res.data;
}

// 通用图片上传（管理端编辑陪玩/老板头像等），返回 url
export async function uploadImage(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axiosForBackend.post('/api/game-items/upload', fd);
  return res.data.url;
}

// 老板端上传自己头像
export async function uploadBossAvatar(file: File, name: string): Promise<string> {
  const fd = new FormData();
  fd.append('name', name);
  fd.append('file', file);
  const res = await axiosForBackend.post('/api/bosses/upload-avatar', fd);
  return res.data.url;
}

// 补全：操作日志
export async function getAuditLogs(params: any = {}) {
  const res = await axiosForBackend.get(`/api/dashboard/audit-logs${getQueryString(params)}`);
  return res.data;
}

export async function restoreDeleted(table: string, id: string) {
  const res = await axiosForBackend.post('/api/dashboard/restore', { table, id });
  return res.data;
}

// 通用 HTTP 方法（供页面直接调用）
export const get = (url: string, config?: any) => axiosForBackend.get(url, config).then(r => r.data);
export const post = (url: string, data?: any, config?: any) => axiosForBackend.post(url, data, config).then(r => r.data);
export const patch = (url: string, data?: any, config?: any) => axiosForBackend.patch(url, data, config).then(r => r.data);
export const put = (url: string, data?: any, config?: any) => axiosForBackend.put(url, data, config).then(r => r.data);
export const del = (url: string, config?: any) => axiosForBackend.delete(url, config).then(r => r.data);

// 派单
export async function assignOrder(id: string, data: { player1?: string; player2?: string }) {
  const res = await axiosForBackend.post(`/api/orders/${id}/assign`, data);
  return res.data;
}

// 取消待接单
export async function cancelPendingOrder(id: string) {
  const res = await axiosForBackend.post(`/api/orders/${id}/cancel-pending`);
  return res.data;
}

// 老板端下单
export async function bossCreateOrder(data: any) {
  const res = await axiosForBackend.post('/api/orders/boss/create-pending', data);
  return res.data;
}

// 老板注册
export async function bossRegister(data: { phone: string; password: string; name: string; wechat?: string }) {
  const res = await axiosForBackend.post('/api/auth/boss-register', data);
  return res.data;
}
